import { useOutletContext, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import type { CommentActionType, CommentData, TPostCommentsElement } from '@shared/dream-components';
import { PostCommentsElement } from '@shared/dream-components';
import { type ReactNode, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import DeleteAction from '~/pages/post/CommentsSection/DeleteAction';
import ReportAction from '~/pages/post/CommentsSection/ReportAction';

import CommentAction from '~/components/CommentAction';
import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import SlideUpModal from '~/components/Modal/SlideUpModal';

import type { Admin } from '~/interfaces/admin';
import type { CommentType } from '~/interfaces/comment';
import { CommentSectionState } from '~/interfaces/comment';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { Subscription } from '~/interfaces/subscriptions';

import type { OrderType } from '~/hooks/useLoaders/usePaginatedComments';
import usePaginatedComments from '~/hooks/useLoaders/usePaginatedComments';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';

import { usePostPageContext } from '~/context/usePostPageContext';
import type { RootLoaderData } from '~/root';
import type { PostLoaderData } from '~/routes/p/$slug';

import Text from '~/components/Text';

const COMMENTS_ORDER_OPTIONS = [
  { label: 'Newest first', value: 'desc' },
  { label: 'Oldest first', value: 'asc' },
  { label: 'Most popular', value: 'popular' },
];

const getLikeId = (admin?: Admin, subscription?: Subscription): string | number => {
  if (admin) {
    return admin?.id;
  }

  if (subscription) {
    return subscription?.id;
  }

  return '';
};

const mapComments = (comments: CommentData[], admin?: Admin, subscription?: Subscription): CommentData[] => {
  return comments.map((comment: CommentData) => ({
    ...comment,
    sub_comments: mapComments(comment.sub_comments || [], admin, subscription),
    is_liked: comment?.likes?.includes(getLikeId(admin, subscription)),
    can_delete: comment?.commenter_id === subscription?.id,
  }));
};

const filterDeletedComments = (comments: CommentData[]): CommentData[] => {
  return comments.filter((comment) => {
    if (comment.deleted_by_commenter_at) {
      return false;
    }

    comment.sub_comments = filterDeletedComments(comment.sub_comments || []);
    return true;
  });
};

interface Props {
  node: TPostCommentsElement;
  children: ReactNode | ReactNode[];
}

const ErrorBoundaryFallback = ({ message }: { message: string }) => (
  <div className="flex max-w-2xl flex-col space-y-2 py-8">
    <Text as="p" type="body" size="md" className="text-center leading-tight tracking-normal">
      {message}
    </Text>
  </div>
);

const PostComments = ({ node, children }: Props) => {
  const { canShowCommentsNode } = usePostPageContext() || { canShowCommentsNode: false };

  const { t } = useTranslation();

  const { handleOpenModal } = useGlobalModals();

  const [commentActionType, setCommentActionType] = useState<CommentActionType | null>(null);
  const [actionableComment, setActionableComment] = useState<CommentData | null>(null);

  const { admin } = useOutletContext<OutletContext>();
  const { subscription } = useRouteLoaderData('root') as RootLoaderData;
  const { post, site, onlyRenderComments } = useTypedLoaderData<PostLoaderData>();

  const [, setSearchParams] = useSearchParams();

  const initialCommentOrder = site?.viewable_site_version?.settings?.comments_ordering as OrderType;

  // Fetch comments for the post
  // This does not run if comments are disabled on this post or on the publication level
  // This also runs on the client so it is not page blocking to render the post. Comments are loaded in the background
  const {
    initialFetched,
    comments,
    optimisticallyRenderNewComment,
    optimisticallyDeleteTopLevelComment,
    optimisticallyDeleteSubComment,
    commentOrder,
    reorderComments,
    loadMore,
    isLoadingMore,
    remainingCount,
    hasNextPage,
  } = usePaginatedComments({
    postId: post?.id || '',
    enabled: canShowCommentsNode,
    order: initialCommentOrder,
  });

  const isSubscriberLoggedIn = Boolean(subscription);
  const isLoggedInAsAdmin = Boolean(admin);
  const isNotLoggedIn = !isLoggedInAsAdmin && !isSubscriberLoggedIn;
  const subscriberAvatarUrl = subscription?.avatar_url;

  const hasNoComments = !comments?.length;
  const isBannedFromPostComments = Boolean(subscription?.comment_post_bans?.includes(post?.id));
  const hasActiveCommentBan = Boolean(subscription?.comment_publication_ban) || isBannedFromPostComments;
  const commentsLocked = post?.comments_state === CommentSectionState.LOCKED || hasActiveCommentBan;
  const isPremiumCommentsOnly = post?.comments_state === CommentSectionState.PREMIUM;
  const needsToSetProfileDetails = !subscription?.comment_details_set && !isLoggedInAsAdmin;
  const showActionsDropdown = !commentsLocked && !needsToSetProfileDetails;

  const renderCommentAction = !commentsLocked || isLoggedInAsAdmin;

  const filteredComments: CommentData[] = useMemo(() => {
    if (!initialFetched) {
      return [];
    }
    return mapComments(filterDeletedComments(comments), admin, subscription);
  }, [admin, comments, subscription, initialFetched]);

  const onCreate = (newComment: CommentType, parentCommentId?: string) => {
    optimisticallyRenderNewComment(newComment, parentCommentId);
  };

  const handleClickLike = async (formData: FormData) => {
    if (isNotLoggedIn) {
      handleOpenModal('login', { preventScrollReset: true });
      return false;
    }

    if (!post || !subscription) {
      toast.error('Something went wrong, please try again!');
      return false;
    }

    formData.append('post_id', post.id);
    formData.append('subscription_id', String(subscription.id));

    if (admin) {
      formData.append('user_id', String(admin.id));
    }

    if (isLoggedInAsAdmin) {
      await fetch('/admins/comment_like', {
        method: 'POST',
        body: formData,
      });
    } else {
      await fetch('/comment_like', {
        method: 'POST',
        body: formData,
      });
    }

    return true;
  };

  const handleClickAction = (action: CommentActionType, comment: CommentData) => {
    setCommentActionType(action);
    setActionableComment(comment);
  };

  const handleClickLogin = () => {
    handleOpenModal('login', { preventScrollReset: true });
  };

  const handleClickSubscribe = () => {
    handleOpenModal('signup', { preventScrollReset: true });
  };

  if (!canShowCommentsNode) {
    return null;
  }

  return (
    <>
      <SlideUpModal
        title={commentActionType === 'delete' ? 'Delete Comment' : 'Report a comment'}
        description={
          commentActionType === 'delete'
            ? 'Are you sure you want to delete the comment?'
            : 'Share with a moderator your concerns about the comment you are reporting'
        }
        isOpen={!!actionableComment}
        onClose={() => {
          setActionableComment(null);
          setCommentActionType(null);
        }}
        backgroundColor={site.publication.web_theme.background_color}
        textColor={site.publication.web_theme.text_on_background_color}
        closeButtonBackgroundColor={site.publication.web_theme.secondary_color}
        closeButtonTextColor={site.publication.web_theme.text_on_secondary_color}
      >
        <>
          {commentActionType === 'delete' && (
            <DeleteAction
              postId={post.id}
              commentId={actionableComment?.id}
              commenterId={actionableComment?.commenter_id}
              subscriptionId={subscription?.id}
              userId={admin?.id}
              isLoggedInAsAdmin={isLoggedInAsAdmin}
              buttonBackgroundColor={site.publication.web_theme.primary_color}
              buttonTextColor={site.publication.web_theme.text_on_primary_color}
              onDelete={() => {
                if (!actionableComment) {
                  return;
                }

                if (actionableComment.depth_level === 1) {
                  optimisticallyDeleteTopLevelComment(actionableComment.id);
                } else {
                  optimisticallyDeleteSubComment(actionableComment.id);
                }

                setActionableComment(null);
                setCommentActionType(null);
              }}
            />
          )}
          {commentActionType === 'report' && (
            <ReportAction
              postId={post.id}
              commentId={actionableComment?.id}
              subscriptionId={subscription?.id}
              onReport={() => {
                setActionableComment(null);
                setCommentActionType(null);
              }}
              textareaTextColor={site.publication.web_theme.primary_color}
              buttonBackgroundColor={site.publication.web_theme.primary_color}
              buttonTextColor={site.publication.web_theme.text_on_primary_color}
            />
          )}
        </>
      </SlideUpModal>
      <ErrorBoundary fallbackRender={(props) => <ErrorBoundaryFallback message={props.error.message} />}>
        <CommentAction
          isPremiumCommentsOnly={isPremiumCommentsOnly}
          onCommentCreated={onCreate}
          isNotLoggedIn={isNotLoggedIn}
        >
          {(isSubmitting, setCurrentComment, replyingToCommentId, setReplyingToCommentId) => (
            <PostCommentsElement
              id="comments"
              isLive
              isSubmitting={isSubmitting}
              element={{
                ...node,
                attrs: {
                  ...node.attrs,
                  data: {
                    comments: filteredComments,
                  },
                },
              }}
              onReorderComments={(newOrder: string) => reorderComments(newOrder as OrderType)}
              commentsOrderOptions={COMMENTS_ORDER_OPTIONS}
              commentOrder={commentOrder || initialCommentOrder}
              showBackToPost={onlyRenderComments}
              onBackToPost={() => setSearchParams({})}
              postTitle={post.web_title}
              onLoadMoreComments={loadMore}
              isLoadingMoreComments={isLoadingMore}
              showLoadMore={initialFetched && hasNextPage && !hasNoComments}
              remainingCommentsCount={remainingCount}
              canComment={renderCommentAction}
              areCommentsLoading={!initialFetched}
              showActions={showActionsDropdown || isLoggedInAsAdmin}
              onClickAction={handleClickAction}
              onClickLike={handleClickLike}
              onClickLogin={handleClickLogin}
              onClickSubscribe={handleClickSubscribe}
              setCurrentComment={setCurrentComment}
              replyingToCommentId={replyingToCommentId}
              setReplyingToCommentId={setReplyingToCommentId}
              isVisitorLoggedIn={!isNotLoggedIn}
              commenterId={subscription?.id}
              translations={{
                'comments.count.singular': t('comments.count.singular', {
                  count: 1,
                }),
                'comments.count.plural': t('comments.count.plural', {
                  count: filteredComments.length || 0,
                }),
                'shared.cta.cancel': t('shared.cta.cancel'),
                'shared.cta.login': t('shared.cta.login'),
                'shared.cta.subscribe': t('shared.cta.subscribe'),
                'shared.cta.or': t('shared.cta.or'),
                'shared.cta.to_participate': t('shared.cta.to_participate'),
                'comments.like': t('comments.like'),
                'comments.replying.reply': t('comments.replying.reply'),
              }}
              avatarUrl={subscriberAvatarUrl}
            >
              {children}
            </PostCommentsElement>
          )}
        </CommentAction>
      </ErrorBoundary>
    </>
  );
};

export default PostComments;
